import api from './api';
import { coerceBlobForPreview } from '../utils/attachmentPreview';

class BlobStorageService {
  /**
   * Upload a single file
   * @param {File} file - File to upload
   * @param {string} folder - Optional folder path
   * @returns {Promise<Object>} Upload result
   */
  async uploadFile(file, folder = '') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      if (folder) {
        formData.append('folder', folder);
      }

      const response = await fetch(`${api.baseURL}/api/blob-storage/upload`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Upload a single file to a specific container
   * @param {File} file - File to upload
   * @param {string} containerName - Container name
   * @param {string} folder - Optional folder path
   * @returns {Promise<Object>} Upload result
   */
  async uploadFileToContainer(file, containerName, folder = '') {
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('containerName', containerName);
      if (folder) {
        formData.append('folder', folder);
      }

      const response = await fetch(`${api.baseURL}/api/blob-storage/upload-to-container`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        // Try to parse error as JSON, but handle cases where it might not be JSON
        let errorData;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            errorData = await response.json();
          } catch (e) {
            // If JSON parsing fails, use the status text
            throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
          }
        } else {
          // If response is not JSON, read as text
          const errorText = await response.text();
          throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Upload multiple files
   * @param {File[]} files - Array of files to upload
   * @param {string} folder - Optional folder path
   * @returns {Promise<Object>} Upload results
   */
  async uploadMultipleFiles(files, folder = '') {
    try {
      const formData = new FormData();
      files.forEach(file => {
        formData.append('files', file);
      });
      if (folder) {
        formData.append('folder', folder);
      }

      const response = await fetch(`${api.baseURL}/api/blob-storage/upload-multiple`, {
        method: 'POST',
        credentials: 'include',
        body: formData,
      });

      if (!response.ok) {
        // Try to parse error as JSON, but handle cases where it might not be JSON
        let errorData;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            errorData = await response.json();
          } catch (e) {
            // If JSON parsing fails, use the status text
            throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
          }
        } else {
          // If response is not JSON, read as text
          const errorText = await response.text();
          throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Download a file
   * @param {string} blobName - Name of the blob to download
   * @returns {Promise<Blob>} File blob
   */
  async downloadFile(blobName) {
    try {
      const response = await fetch(`${api.baseURL}/api/blob-storage/download/${encodeURIComponent(blobName)}`, {
        method: 'GET',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.blob();
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get file metadata
   * @param {string} blobName - Name of the blob
   * @returns {Promise<Object>} File metadata
   */
  async getFileMetadata(blobName) {
    try {
      const response = await api.request(`/blob-storage/metadata/${encodeURIComponent(blobName)}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * List files in a folder
   * @param {string} folder - Optional folder path
   * @returns {Promise<Array>} List of files
   */
  async listFiles(folder = '') {
    try {
      const queryParams = folder ? `?folder=${encodeURIComponent(folder)}` : '';
      const response = await api.request(`/blob-storage/list${queryParams}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete a file
   * @param {string} blobName - Name of the blob to delete
   * @returns {Promise<Object>} Delete result
   */
  async deleteFile(blobName) {
    try {
      const response = await api.request(`/blob-storage/delete/${encodeURIComponent(blobName)}`, {
        method: 'DELETE',
      });
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Generate a SAS URL for temporary access
   * @param {string} blobName - Name of the blob
   * @param {number} expiresInHours - Hours until URL expires (default: 1)
   * @returns {Promise<string>} SAS URL
   */
  async generateSasUrl(blobName, expiresInHours = 1) {
    try {
      const response = await api.request(`/blob-storage/sas-url/${encodeURIComponent(blobName)}?expiresInHours=${expiresInHours}`);
      return response.data.sasUrl;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Check blob storage health
   * @returns {Promise<Object>} Health status
   */
  async checkHealth() {
    try {
      const response = await api.request('/blob-storage/health');
      return response.data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Download and save file to local filesystem
   * @param {string} blobName - Name of the blob to download
   * @param {string} fileName - Name to save the file as
   */
  async downloadAndSaveFile(blobName, fileName) {
    try {
      const blob = await this.downloadFile(blobName);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Fetch a blob from a specific container (same endpoint as download; does not save to disk).
   * @param {string} blobName - Name of the blob
   * @param {string} containerName - Container name
   * @returns {Promise<Blob>}
   */
  async fetchBlobFromContainer(blobName, containerName) {
    const response = await fetch(`${api.baseURL}/api/blob-storage/download-from-container/${encodeURIComponent(blobName)}?container=${encodeURIComponent(containerName)}`, {
      credentials: 'include'
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    return response.blob();
  }

  /**
   * Fetch blob for in-browser preview (inline Content-Disposition, correct MIME).
   */
  async fetchBlobForViewFromContainer(blobName, containerName) {
    const response = await fetch(
      `${api.baseURL}/api/blob-storage/view-from-container/${encodeURIComponent(blobName)}?container=${encodeURIComponent(containerName)}`,
      {
        credentials: 'include'
      }
    );

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const raw = await response.blob();
    const headerType = (response.headers.get('content-type') || '').split(';')[0].trim();
    const mime = headerType || raw.type || 'application/octet-stream';
    let blob = raw.type === mime ? raw : new Blob([await raw.arrayBuffer()], { type: mime });
    return coerceBlobForPreview(blob, blobName);
  }

  /**
   * Download and save file from specific container to local filesystem
   * @param {string} blobName - Name of the blob to download
   * @param {string} containerName - Container name
   * @param {string} fileName - Name to save the file as
   */
  async downloadAndSaveFileFromContainer(blobName, containerName, fileName) {
    try {
      const blob = await this.fetchBlobFromContainer(blobName, containerName);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      throw error;
    }
  }

  /**
   * Delete file from specific container
   * @param {string} blobName - Name of the blob to delete
   * @param {string} containerName - Container name
   * @returns {Promise<Object>} Delete result
   */
  async deleteFileFromContainer(blobName, containerName) {
    try {
      const response = await fetch(`${api.baseURL}/api/blob-storage/delete-from-container/${encodeURIComponent(blobName)}?container=${encodeURIComponent(containerName)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        // Try to parse error as JSON, but handle cases where it might not be JSON
        let errorData;
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          try {
            errorData = await response.json();
          } catch (e) {
            // If JSON parsing fails, use the status text
            throw new Error(`HTTP error! status: ${response.status} ${response.statusText}`);
          }
        } else {
          // If response is not JSON, read as text
          const errorText = await response.text();
          throw new Error(errorText || `HTTP error! status: ${response.status}`);
        }
        throw new Error(errorData.message || errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      throw error;
    }
  }

  /**
   * Get file size in human readable format
   * @param {number} bytes - File size in bytes
   * @returns {string} Human readable file size
   */
  formatFileSize(bytes) {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  /**
   * Get file icon based on content type
   * @param {string} contentType - MIME type
   * @returns {string} Icon name
   */
  getFileIcon(contentType) {
    if (contentType.startsWith('image/')) return 'image';
    if (contentType === 'application/pdf') return 'picture_as_pdf';
    if (contentType.includes('word')) return 'description';
    if (contentType.includes('excel') || contentType.includes('spreadsheet')) return 'table_chart';
    if (contentType.includes('zip') || contentType.includes('compressed')) return 'folder_zip';
    if (contentType === 'text/plain') return 'article';
    return 'insert_drive_file';
  }
}

export default new BlobStorageService(); 




